import React, { useEffect, useState, useRef } from "react";
import {
  AppBar,
  Badge,
  Button,
  CircularProgress,
  ClickAwayListener,
  colors,
  Divider,
  Grid,
  Grow,
  Hidden,
  IconButton,
  InputAdornment,
  Popper,
  TextField,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { AccountCircle, AssignmentTurnedIn } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import "./navbar.css";
import logo from "../../../assets/logo2.png";
import { Product } from "../../../classes/types/product/product";
import { ProductHandler } from "../../../classes/methods/productMethods/productMethods";
import { AuthHandler } from "../../../classes/methods/authMethods/authMethods";
import { User } from "../../../classes/types/user/user";
import { Category } from "../../../classes/types/category/category";
import { CategoryHandler } from "../../../classes/methods/categoryMethods/categoryMethods";
import SearchCard from "../../customComponents/searchCard/searchCard";
import LoginEvent from "../../../classes/events/LoginEvent";
import { Skeleton } from "@mui/material";
import CartEvents from "../../../classes/events/CartEvents";
import { CartHandler } from "../../../classes/methods/cartMethods/cartMethods";
import { LaterListHandler } from "../../../classes/methods/laterListMethods/laterListMethods";
import LaterListEvents from "../../../classes/events/LaterListEvents";

const useStyles = makeStyles({
  paper: {
    color: "black",
    paddingLeft: "2%",
    paddingRight: "2%",
    height: "100px",
  },
  nested: {
    paddingLeft: "20px",
  },
  nested2: {
    paddingLeft: "20px",
  },
  root: {
    width: "100%",
    left: "10vw",
  },
});

interface NavbarProps {
  searchInputProp?: string;
}

const Navbar: React.FC<NavbarProps> = ({ searchInputProp }) => {
  const [selectedTab, setSelectedTab] = useState<number | false>(false);
  const [searchInput, setSearchInput] = useState(searchInputProp || "");
  const [badgeCart, setBadgeCart] = useState(0);
  const [badgeLaterList, setBadgeLaterList] = useState(0);
  const [menu, setMenu] = useState(false);
  const [menuCat, setMenuCat] = useState(false);
  const [searchMenu, setSearchMenu] = useState(false);
  const [filteredProduct, setFilteredProduct] = useState<Product[]>([]);
  const [loggedUser, setLoggedUser] = useState<User>(new User());
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [unicoLoading, setUnicoLoading] = useState(false);
  const [unicoProducts, setUnicoProducts] = useState<Product[]>([]);

  const navigate = useNavigate();
  const myRef = useRef<HTMLAnchorElement | null>(null);
  const myRef2 = useRef<HTMLInputElement | null>(null);
  const myRef3 = useRef<HTMLAnchorElement | null>(null);

  const styles = useStyles();

  // Event listener per invio ricerca
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && searchInput.length >= 3) {
        closeSearchMenu();
        navigate(`/shop/cerca/${encodeURIComponent(searchInput)}/page/1`);
        setSearchInput("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [searchInput, navigate]);

  // Aggiorna dati su mount e login/cart events
  useEffect(() => {
    refreshData();
    LoginEvent.addLoginCallback(onLogin);
    CartEvents.addCartModifiedEvent(onCartUpdated);
    LaterListEvents.addLaterListModifiedEvent(onLaterListUpdated);
    return () => {
      LoginEvent.removeLoginCallback(onLogin);
      CartEvents.removeCartModifiedEvent(onCartUpdated);
      LaterListEvents.removeLaterListModifiedEvent(onLaterListUpdated);
    };
  }, []);

  const onLogin = () => refreshData();

  const openMenu = () => { setMenu(true); setMenuCat(false); };
  const closeMenu = () => setMenu(false);
  const openMenuCat = () => { setMenuCat(true); setMenu(false); };
  const closeMenuCat = () => setMenuCat(false);
  const openSearchMenu = () => setSearchMenu(true);
  const closeSearchMenu = () => setSearchMenu(false);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    filterData(e.target.value);
  };

  const filterData = (value: string) => {
    if (value.length >= 3) {
      setSearchLoading(true);
      ProductHandler.searchProducts(value).then(products => {
        setFilteredProduct(products);
        openSearchMenu();
        setSearchLoading(false);
      });
    } else {
      closeSearchMenu();
    }
  };

  const refreshData = () => {
    setLoading(true);
    setCategoriesLoading(true);
    setUnicoLoading(true);

    ProductHandler.GetUnicoProducts()
      .then(newUnicoProducts => {
        newUnicoProducts.sort((a, b) => a.name.localeCompare(b.name));
        setUnicoProducts(newUnicoProducts);
        setUnicoLoading(false);
      })
      .catch(() => setUnicoLoading(false));

    CategoryHandler.getCategoriesFather()
      .then(newCategories => {
        newCategories.sort((a, b) => a.name.localeCompare(b.name));
        setCategories(newCategories);
        setCategoriesLoading(false);
      })
      .catch(() => setCategoriesLoading(false));

    AuthHandler.isLogged()
      .then(response => {
        if (!response) {
          setLoggedUser(new User());
          setLoading(false);
        } else {
          CartHandler.getCartItemNumber()
            .then(cartItemNumber => {
              LaterListHandler.GetLaterListItemNumber()
                .then(laterListItemNumber => {
                  setBadgeCart(cartItemNumber);
                  setBadgeLaterList(laterListItemNumber);
                  setLoggedUser(response);
                  setLoading(false);
                })
                .catch(() => setLoading(false));
            })
            .catch(() => {
              setLoggedUser(new User());
              setLoading(false);
            });
        }
      })
      .catch(() => {
        setLoggedUser(new User());
        setLoading(false);
      });
  };

  const onCartUpdated = () => {
    CartHandler.getCartItemNumber().then(setBadgeCart).catch(() => setBadgeCart(0));
  };

  const onLaterListUpdated = () => {
    LaterListHandler.GetLaterListItemNumber().then(setBadgeLaterList).catch(() => setBadgeLaterList(0));
  };

  const getSearchData = () => (
    <div>
      <div style={{ width: "58vw", maxHeight: "70vh", textAlign: "left", overflow: "auto" }}>
        <Grid container direction="row" alignContent="center" justifyContent="flex-start">
          {filteredProduct.length > 0 ? (
            filteredProduct.slice(0, 20).map((product, index) => (
              <div key={index} style={{ paddingRight: "10px", width: "100%" }} onClick={closeSearchMenu}>
                <SearchCard searchedInput={searchInput} product={product} isLogged={loggedUser.id !== ""} />
                <hr />
              </div>
            ))
          ) : (
            <div style={{ padding: "1vw", fontSize: "20px", color: "grey", textAlign: "center" }}>
              Nessun prodotto trovato!
            </div>
          )}
        </Grid>
      </div>
      {filteredProduct.length > 0 && (
        <Grid container justifyContent="center" style={{ padding: "10px 10px 0 10px" }}>
          <Link to={`/shop/cerca/${encodeURIComponent(searchInput)}/page/1`}>
            <Button color="primary" onClick={closeSearchMenu}>
              Visualizza Tutti i {filteredProduct.length} prodotti
            </Button>
          </Link>
        </Grid>
      )}
    </div>
  );

  return (
    <div style={{ height: "100px" }}>
      {loading ? (
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-around", height: "fit-content", paddingTop: "1%", paddingBottom: "1%" }}>
          <div style={{ width: "8%" }} />
          <Skeleton width={"12%"} height={70} animation="wave" />
          <Skeleton width={"50%"} height={70} animation="wave" />
          <Skeleton width={"5%"} height={70} animation="wave" />
          <Skeleton width={"5%"} height={70} animation="wave" />
          <Skeleton width={"10%"} height={70} animation="wave" />
        </div>
      ) : (
        <><AppBar elevation={0} style={{ display: "flex", backgroundColor: "white", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }} classes={{ root: styles.paper }}>
          {/* Logo */}
          <Hidden only={["xs"]}>
            <h1 style={{ cursor: "pointer", marginTop: "47px", marginRight: "2%", textAlign: "left", paddingBottom: "1%" }}>
              <Link to="/"><img alt="logo" src={logo} /></Link>
            </h1>
          </Hidden>
          {/* Menu buttons */}
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <Button
              component={Link}
              to="/unico"
              ref={myRef}
              aria-haspopup
              onMouseEnter={openMenu}
              onClick={() => closeMenu()}
              disabled={categoriesLoading || unicoLoading}
            >
              Sistemi Unico
            </Button>
            <div style={{ height: "30px" }}><Divider orientation="vertical" style={{ height: "100%" }} /></div>
            <Button
              component={Link}
              to="/categories" // o dove vuoi
              ref={myRef3}
              aria-haspopup
              onMouseEnter={openMenuCat}
              disabled={categoriesLoading || unicoLoading}
            >
              Catalogo
            </Button>
          </div>
          <div
            style={{
              display: "inline",
              alignSelf: "center",
              marginLeft: "2%",
              marginRight: "2%",
            }}
          >
            <TextField
              ref={myRef2}
              autoComplete="off"
              placeholder="Cerca..."
              variant="standard"
              value={searchInput}
              onChange={onSearchChange}
              onFocus={() => filterData(searchInput)}
              style={{ width: "50vw", boxShadow: "3px 8px 15px 0.1px #ECECEC", borderRadius: "30px", padding: "7px" }}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Link to={searchInput.length > 3 ? `/shop/cerca/${encodeURIComponent(searchInput)}/page/1` : ""}>
                      <SearchIcon style={{ cursor: "pointer" }} color="primary" />
                    </Link>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="start">
                    <CloseIcon style={{ cursor: "pointer" }} onClick={() => setSearchInput("")} color="secondary" />
                  </InputAdornment>
                ),
              }} />
          </div>

          {/* Cart and user */}
          <div>
            <Link to="/carrello">
              <IconButton className={"nav-cart"} color="inherit">
                <Badge badgeContent={badgeCart} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Link>
          </div>
          <div>
            <Link to="/shop/saved/page/1">
              <IconButton className={"nav-cart"} color="inherit">
                <Badge badgeContent={badgeLaterList} color="primary">
                  <AssignmentTurnedIn />
                </Badge>
              </IconButton>
            </Link>
          </div>
          <div>
            {loggedUser.id ? (
              <Link to={"/ordini"}>
                <IconButton
                  edge="end"
                  color="inherit"
                  className={"nav-user"}
                  disableTouchRipple={true}
                  style={{ backgroundColor: "transparent !important" }}
                >
                  <AccountCircle className={"navbar-user"} />{" "}
                  <h2
                    style={{
                      display: "inline-block",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      width: "auto",
                      maxWidth: "9.4vw",
                      whiteSpace: "nowrap",
                    }}
                    className={"navbar-user-font"}
                  >
                    <Hidden only={["xs", "sm"]}>
                      {loggedUser.name}
                    </Hidden>
                  </h2>
                </IconButton>
              </Link>
            ) : (
              <Link to={"/login"} style={{ color: 'black !important' }}>
                <IconButton
                  edge="end"

                  className={"nav-user"}
                  disableTouchRipple={true}
                  style={{ backgroundColor: "transparent !important", color: "black !important" }}
                >
                  <AccountCircle className={"navbar-user"} />{" "}
                  <h2 className={"navbar-user-font"}>
                    <Hidden only={["xs", "sm"]}>Login</Hidden>
                  </h2>
                </IconButton>
              </Link>

            )}
          </div>
        </AppBar>
          <div>

            <Popper
              onMouseLeave={closeMenuCat} // senza this
              style={{ zIndex: 9999 }}
              className="category-popper"
              open={menuCat} // usa menuCat dallo state
              anchorEl={myRef3.current}
              placement="bottom-start"
              role={undefined}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{ transformOrigin: placement === "bottom" ? "top start" : "top start" }}
                >
                  <div style={{ boxShadow: "3px 12px 25px 0.1px #4e4c4e" }} className="navbar-category-container">
                    <ClickAwayListener onClickAway={closeMenuCat}>
                      <div>
                        <div className="navbar-category-title2">Catalogo</div>
                        <div className="navbar-category-values-container">
                          <Grid container alignContent="flex-start" direction="row" justifyContent="flex-start">
                            {categories.map((category, index) => (
                              <div key={category.name + index} className="navbar-category-text-container">
                                <Link to={`/shop/category/${category.externalCategoryId}/page/1`}>
                                  <span className="navbar-category-text">{category.name}</span>
                                </Link>
                              </div>
                            ))}
                          </Grid>
                        </div>
                      </div>
                    </ClickAwayListener>
                  </div>
                </Grow>
              )}
            </Popper>

            <div style={{ height: "100px" }}>
              <AppBar
                elevation={0}
                style={{
                  display: "flex",
                  backgroundColor: "white",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 20px",
                  height: "100px",
                }}
              >
                {/* Logo */}
                <Hidden only={["xs"]}>
                  <h1 style={{ cursor: "pointer" }}>
                    <Link to="/"><img alt="logo" src={logo} /></Link>
                  </h1>
                </Hidden>

                {/* Menu Buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Button
                    component={Link}
                    to="/unico"
                    ref={myRef}
                    aria-haspopup
                    onMouseEnter={openMenu}
                    onClick={() => closeMenu()}
                    disabled={categoriesLoading || unicoLoading}
                  >
                    Sistemi Unico
                  </Button>
                  <Divider orientation="vertical" flexItem />
                  <Button
                    component={Link}
                    to="/categories" // o dove vuoi
                    ref={myRef3}
                    aria-haspopup
                    onMouseEnter={openMenuCat}
                    disabled={categoriesLoading || unicoLoading}
                  >
                    Catalogo
                  </Button>
                </div>

                {/* Search Bar */}
                <TextField
                  ref={myRef2}
                  autoComplete="off"
                  placeholder="Cerca..."
                  variant="standard"
                  value={searchInput}
                  onChange={onSearchChange}
                  onFocus={() => { filterData(searchInput); openSearchMenu(); }}
                  style={{
                    width: "50vw",
                    boxShadow: "3px 8px 15px 0.1px #ECECEC",
                    borderRadius: "30px",
                    padding: "7px",
                  }}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Link to={searchInput.length > 3 ? `/shop/cerca/${encodeURIComponent(searchInput)}/page/1` : ""}>
                          <SearchIcon style={{ cursor: "pointer" }} color="primary" />
                        </Link>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <CloseIcon
                          style={{ cursor: "pointer" }}
                          onClick={() => setSearchInput("")}
                          color="secondary" />
                      </InputAdornment>
                    ),
                  }} />

                {/* Icon Cart & User */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", color: 'black' }}>
                  <Link to="/carrello">
                    <IconButton color="inherit">
                      <Badge badgeContent={badgeCart} color="primary">
                        <ShoppingCartIcon />
                      </Badge>
                    </IconButton>
                  </Link>

                  <Link to="/shop/saved/page/1">
                    <IconButton color="inherit">
                      <Badge badgeContent={badgeLaterList} color="primary">
                        <AssignmentTurnedIn />
                      </Badge>
                    </IconButton>
                  </Link>

                  <Link to={loggedUser.id ? "/ordini" : "/login"}>
                    <IconButton color="inherit">
                      <AccountCircle />
                      <Hidden only={["xs", "sm"]}>
                        {loggedUser.id ? (
                          <span style={{ fontSize: "1.2rem" }}>
                            {loggedUser.name.length > 15
                              ? loggedUser.name.slice(0, 15) + "..."
                              : loggedUser.name}
                          </span>
                        ) : (
                          <span style={{ fontSize: "1.2rem" }}>Login</span>
                        )}
                      </Hidden>
                    </IconButton>
                  </Link>
                </div>
              </AppBar>

              {/* Popper Sistemi Unico */}
              <Popper
                onMouseLeave={closeMenu}
                open={menu}
                anchorEl={myRef.current}
                placement="bottom-start"
                transition
                disablePortal
                className={"category-popper"}
                style={{ zIndex: 9999, width: "30vw" }}


              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps} style={{ transformOrigin: "top start" }}>
                    <div
                      style={{ boxShadow: "3px 12px 25px 0.1px #4e4c4e" }}
                      className={"navbar-category-container"}
                    >
                      <ClickAwayListener onClickAway={closeMenu}>
                        <div style={{ padding: "10px" }}>
                          <div className={"navbar-category-title"}>
                            Sistemi Unico</div>
                          <div style={{ maxHeight: "22vh", overflow: "auto", padding: "10px" }}>
                            <Grid alignContent={"flex-start"}
                              direction={"row"}
                              justifyContent={"flex-start"}
                              container spacing={1}>
                              {unicoProducts.map((p: any, i: number) => {



                                return (
                                  <div
                                    key={p.name + i}
                                    id={"navbar-category-text-container"}
                                    className={
                                      "navbar-category-text-container"
                                    }
                                  >
                                    <Link
                                      to={
                                        "/shop/home/prodotto/" +
                                        p.id
                                      }
                                    >
                                      <Grid container>
                                        <Grid
                                          justifyContent={"flex-start"}
                                          alignContent={"flex-start"}
                                          xs={12}
                                          container
                                          item
                                        >
                                          <span
                                            className={"navbar-category-text"}
                                          >
                                            {p.name}
                                          </span>
                                        </Grid>
                                      </Grid>
                                    </Link>
                                  </div>
                                );
                              })}

                            </Grid>
                          </div>
                        </div>
                      </ClickAwayListener>
                    </div>
                  </Grow>
                )}
              </Popper>

              {/* Popper Catalogo */}
              <Popper
                onMouseLeave={closeMenuCat}
                open={menuCat}
                anchorEl={myRef3.current}
                placement="bottom-start"
                transition
                disablePortal
                style={{ zIndex: 9999, width: "30vw" }}
                role={undefined}
                className={"category-popper"}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps} style={{ transformOrigin: "top start" }}>
                    <div
                      style={{ boxShadow: "3px 12px 25px 0.1px #4e4c4e" }}
                      className={"navbar-category-container"}
                    >
                      <ClickAwayListener onClickAway={closeMenuCat}>
                        <div style={{ padding: "10px" }}>
                          <div className={"navbar-category-title2"}>Catalogo</div>
                          <div style={{ maxHeight: "22vh", overflow: "auto", padding: "10px" }}>

                            <Grid container direction="row" spacing={1}>
                              {categories.map((c: any, i: number) => {
                                return (
                                  <div
                                    key={c.name + i}
                                    id={"navbar-category-text-container"}
                                    className={"navbar-category-text-container"}
                                  >
                                    <Link
                                      to={
                                        "/shop/category/" +
                                        c.externalCategoryId +
                                        "/page/1"
                                      }
                                    >
                                      <Grid container>
                                        <Grid
                                          justifyContent={"flex-start"}
                                          alignContent={"flex-start"}
                                          xs={10}
                                          container
                                          item
                                        >
                                          <span
                                            className={"navbar-category-text"}
                                          >
                                            {c.name}
                                          </span>
                                        </Grid>
                                      </Grid>
                                    </Link>
                                  </div>
                                );
                              })}
                            </Grid>
                          </div>
                        </div>
                      </ClickAwayListener>
                    </div>
                  </Grow>
                )}
              </Popper>
              {(searchLoading || searchInput.length >= 3) && (
                <Popper
                  open={searchMenu}
                  anchorEl={myRef2.current}
                  placement="bottom"
                  transition
                  disablePortal
                  style={{ zIndex: 9999 }}
                >
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: "center bottom" }}>
                      <div style={{ marginTop: 10, padding: "10px", boxShadow: "3px 8px 15px 0.1px #ECECEC", backgroundColor: "white" }}>
                        {searchLoading ? (
                          <div style={{ textAlign: "center", padding: 20 }}>
                            <CircularProgress />
                          </div>
                        ) : (
                          <ClickAwayListener onClickAway={closeSearchMenu}>
                            {getSearchData()}
                          </ClickAwayListener>
                        )}
                      </div>
                    </Grow>
                  )}
                </Popper>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};


export default Navbar;
